import { useParams, useLocation, Link } from 'react-router-dom';
import { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { CardsContext } from '../context/CardsContext';
import { SiteContentContext } from '../context/SiteContentContext';

import AutoCarousel from '../components/AutoCarousel';
import Icon from '../components/Icon';
import DifficultyIcons from '../components/DifficultyIcons';
import YandexMap from '../components/YandexMap';
import QuestionModal from '../components/QuestionModal';
import BookingModal from '../components/BookingModal';
import PricePopup from '../components/PricePopup';
import { useGeo, mapUrl, coordsMapUrl } from '../context/GeoContext';
import { injectIymcsMark } from '../utils/richText';
import { useSeo, useJsonLd, toDescription, absUrl, BASE } from '../hooks/useSeo';
import '../styles/card_page.css';

const safe = (html) => ({ __html: injectIymcsMark(DOMPurify.sanitize(html || '')) });

const MONTHS = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];

function nearestDate(dates) {
  const today = new Date().toISOString().slice(0, 10);
  const future = (dates || []).map(d => typeof d === 'object' ? d.isoDate : d).filter(d => d >= today).sort();
  if (!future.length) return null;
  const [y, m, day] = future[0].split('-');
  return `${Number(day)} ${MONTHS[Number(m)-1]} ${y}`;
}

// Склонение: 1 день, 2-4 дня, 5-20 дней...
function pluralDays(n) {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 14) return `${n} дней`;
  if (last === 1) return `${n} день`;
  if (last >= 2 && last <= 4) return `${n} дня`;
  return `${n} дней`;
}

const PATH_TYPE = { study: 'study', adventures: 'adventures', races: 'races', news: 'main', card: null };

const CardPage = ({ isPreview = false }) => {
  const { cardId } = useParams();
  const location = useLocation();
  const { cards, productionCards } = useContext(CardsContext);
  const { content } = useContext(SiteContentContext);
  const yachts = content.yachts || [];
  const instructors = content.instructors || [];

  const country = useGeo();
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const source = isPreview ? cards : productionCards;

  // Determine which section to search based on URL path
  const pathSegments = location.pathname.replace('/preview/', '/').split('/').filter(Boolean);
  const sectionFromPath = PATH_TYPE[pathSegments[0]];

  let card;
  if (sectionFromPath !== undefined && sectionFromPath !== null) {
    // Search only in the correct section
    card = (source[sectionFromPath] || []).find(c => c.slug === cardId || String(c.id) === String(cardId));
  }
  if (!card) {
    // Fallback: search all sections (legacy /card/ routes)
    const allCards = [
      ...(source.main || []),
      ...(source.adventures || []),
      ...(source.study || []),
      ...(source.races || []),
      ...(source.reviews || [])
    ];
    card = allCards.find(c => c.slug === cardId || String(c.id) === String(cardId));
  }

  // SEO: уникальные title/description для конкретной карточки (курс/путешествие/регата).
  useSeo(card ? {
    title: card.title,
    description: toDescription(card.descriptionFull || card.description)
      || [card.title, card.location].filter(Boolean).join(' — '),
    image: card.image,
  } : { title: 'Страница не найдена' });

  // Тип карточки для Schema.org (какой массив её содержит).
  const cardTypeKey =
    (source.study      || []).some(c => c === card) ? 'study'
    : (source.races    || []).some(c => c === card) ? 'races'
    : (source.adventures || []).some(c => c === card) ? 'adventures'
    : null;

  // Дата начала для Event (ближайшая будущая из availableDates либо startDate).
  const eventStart = (() => {
    const iso = (card?.availableDates || [])
      .map(d => (typeof d === 'object' ? d.isoDate : d))
      .filter(Boolean).sort()[0];
    return iso || card?.startDate || null;
  })();

  const orgRef = { '@type': 'Organization', name: 'The Sailing Doctor', url: BASE };
  const priceOffer = card?.price
    ? { '@type': 'Offer', price: String(card.price).replace(/[^\d]/g, '') || undefined, priceCurrency: /€|eur/i.test(card.price) ? 'EUR' : 'RUB', url: BASE + location.pathname.replace(/^\/preview/, '') }
    : undefined;

  useJsonLd(!card ? null : cardTypeKey === 'study'
    ? {
        '@type': 'Course',
        name: card.title,
        description: toDescription(card.descriptionFull || card.description, 300) || card.title,
        provider: orgRef,
        image: card.image ? absUrl(card.image) : undefined,
        offers: priceOffer,
      }
    : {
        '@type': 'Event',
        name: card.title,
        description: toDescription(card.descriptionFull || card.description, 300) || card.title,
        startDate: eventStart || undefined,
        eventStatus: 'https://schema.org/EventScheduled',
        location: card.location ? { '@type': 'Place', name: card.location } : undefined,
        image: card.image ? absUrl(card.image) : undefined,
        organizer: orgRef,
        offers: priceOffer,
      });

  const formatDate = (dateStr) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateStr).toLocaleDateString('ru-RU', options);
  };

  if (!card) {
    return (
      <main style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Карточка не найдена</h2>
        <p>Запрошенная карточка не существует или была удалена</p>
      </main>
    );
  }

  const getCardType = () => {
    if ((source.adventures || []).some(c => c.slug === cardId || String(c.id) === String(cardId))) return 'Путешествие';
    if ((source.study || []).some(c => c.slug === cardId || String(c.id) === String(cardId))) return 'Обучение';
    if ((source.races || []).some(c => c.slug === cardId || String(c.id) === String(cardId))) return 'Регата';
    return 'Новости';
  };

  const getTypeKey = () => {
    if ((source.adventures || []).some(c => c.slug === cardId || String(c.id) === String(cardId))) return 'adventures';
    if ((source.study || []).some(c => c.slug === cardId || String(c.id) === String(cardId))) return 'study';
    if ((source.races || []).some(c => c.slug === cardId || String(c.id) === String(cardId))) return 'races';
    return null;
  };

  const cardForBooking = card ? { ...card, _type: getTypeKey() } : null;

  const allImages = [card.image, ...(card.gallery || [])];

  const nextImage = () => {
    const next = (currentImageIndex + 1) % allImages.length;
    setCurrentImageIndex(next);
    setSelectedImage(allImages[next]);
  };

  const prevImage = () => {
    const prev = (currentImageIndex - 1 + allImages.length) % allImages.length;
    setCurrentImageIndex(prev);
    setSelectedImage(allImages[prev]);
  };

  return (
    <>
    <main className="card-page">
      <section className="main-photo-section">
        <div
          className="main-photo"
          style={{ backgroundImage: `url(${card.image})` }}
        >
          <div className="photo-overlay">
            <h1>{card.title}</h1>
            {card.date && <h3 className="card-date">{card.date}</h3>}
            <span className="card-type">{getCardType()}</span>
          </div>
        </div>
      </section>
      <section className="card-info">
        <div className="info-grid">
          {card.startDate && (
            <div className="info-item">
              <span className="info-label"><Icon name="calendar" size={15}/> Даты</span>
              <span className="info-value">
                {formatDate(card.startDate)}
                {card.endDate && ` — ${formatDate(card.endDate)}`}
              </span>
            </div>
          )}
          {(card.durationDays || card.duration) && (
            <div className="info-item">
              <span className="info-label"><Icon name="clock" size={15}/> Продолжительность</span>
              {/* durationDays — актуальное поле из админки; duration — устаревшее (не редактируется), только как запасное */}
              <span className="info-value">{card.durationDays ? pluralDays(card.durationDays) : card.duration}</span>
            </div>
          )}
          {card.totalSpaces && card.type !== 'races' && (
            <div className="info-item">
              <span className="info-label"><Icon name="people" size={15}/> Места</span>
              <span className="info-value">
                {card.availableSpaces !== undefined
                  ? `${card.availableSpaces}/${card.totalSpaces}`
                  : 'Не ограничено'}
              </span>
            </div>
          )}
          {card.location && (
            <div className="info-item">
              <span className="info-label"><Icon name="pin" size={15}/> Локация</span>
              {card.locationClickable ? (
                <a href={card.locationCoords ? coordsMapUrl(card.locationCoords, country) : mapUrl(card.location, country)} target="_blank" rel="noopener noreferrer" className="info-value info-link">
                  {card.location}
                </a>
              ) : (
                <span className="info-value">{card.location}</span>
              )}
            </div>
          )}
          {(card.yachtName || card.yachtId) && (() => {
            const yacht = card.yachtId ? yachts.find(y => String(y.id) === String(card.yachtId)) : null;
            const name = yacht ? yacht.name : card.yachtName;
            return name ? (
              <div className="info-item">
                <span className="info-label"><Icon name="yacht" size={15}/> Яхта</span>
                {yacht ? (
                  <Link to={`/yacht/${yacht.name.toLowerCase().replace(/\s+/g, '-')}`} className="info-value info-link">{name}</Link>
                ) : (
                  <span className="info-value">{name}</span>
                )}
              </div>
            ) : null;
          })()}
          {(card.captain || card.instructorId) && (() => {
            const inst = card.instructorId ? instructors.find(i => String(i.id) === String(card.instructorId)) : null;
            const name = inst ? inst.name : card.captain;
            const inst2 = card.instructorId2 ? instructors.find(i => String(i.id) === String(card.instructorId2)) : null;
            const name2 = inst2 ? inst2.name : card.captain2;
            return name ? (
              <div className="info-item">
                <span className="info-label"><Icon name="anchor" size={15}/> Инструктор{name2 ? 'ы' : ''}</span>
                <span className="info-value">
                  {inst ? (
                    <Link to={`/instructor/${inst.slug || inst.id}`} className="info-link">{name}</Link>
                  ) : name}
                  {name2 && (
                    <>{' · '}{inst2 ? (
                      <Link to={`/instructor/${inst2.slug || inst2.id}`} className="info-link">{name2}</Link>
                    ) : name2}</>
                  )}
                </span>
              </div>
            ) : null;
          })()}
          {card.miles && (
            <div className="info-item">
              <span className="info-label"><Icon name="miles" size={15}/> Морских миль</span>
              <span className="info-value">{card.miles}</span>
            </div>
          )}
          {card.price && (
            <div className="info-item price">
              <span className="info-label"><Icon name="money" size={15}/> Стоимость</span>
              <span className="info-value"><Link to={isPreview ? '/preview/pricing' : '/pricing'} className="card-price-link">{card.price}</Link></span>
            </div>
          )}
          {card.price && <PricePopup isPreview={isPreview} />}
          {(card.badges || []).map((b, i) => (
            <div key={`badge-${i}`} className="info-item">
              <span className="info-label"><Icon name={b.icon} size={15}/> {b.text}</span>
            </div>
          ))}
          {card.difficulty > 0 && (
            <div className="info-item">
              <span className="info-label">Сложность</span>
              <span className="info-value card-difficulty">
                <DifficultyIcons
                  difficulty={card.difficulty}
                  sectionKey={card.type === 'adventure' ? 'adventures' : card.type === 'study' ? 'study' : 'races'}
                  size={18}
                />
              </span>
            </div>
          )}
        </div>
      </section>
      <section className={`description-section${card.descriptionWide ? ' description-section--wide' : ''}`}>
        <div dangerouslySetInnerHTML={safe(card.description)} />
        {card.descriptionFull && (
          <div className="description-full" dangerouslySetInnerHTML={safe(card.descriptionFull)} />
        )}
        {card.availableDates && card.availableDates.length > 0 && (() => {
          const nearest = nearestDate(card.availableDates);
          return (
            <div className="card-signup-block">
              {nearest && (
                <div className="card-signup-date">
                  Ближайшая дата: <strong>{nearest}</strong>
                </div>
              )}
              <button className="cta-button card-signup-btn" onClick={() => setIsBookingOpen(true)}>
                Записаться
              </button>
              {card.scheduleText && (
                <Link to={isPreview ? '/preview/schedule' : '/schedule'} className="card-schedule-link">
                  {card.scheduleText}
                </Link>
              )}
            </div>
          );
        })()}
      </section>
      {(card.routeDescription || (card.route && card.route.length > 0)) && (
        <section className="route-section">
          <h2>Маршрут</h2>
          {card.routeDescription && (
            <div className="route-desc-html" dangerouslySetInnerHTML={safe(card.routeDescription)} />
          )}
          {card.route && card.route.length > 0 && (
            <div className="route-timeline">
              {card.route.map((point, index) => (
                <div key={index} className="route-point">
                  <div className="point-header">
                    <div className="point-day">{point.day}</div>
                    <div className="point-location">{point.location}</div>
                  </div>
                  <div className="point-description">{point.description}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
      {card.includes && (
        <section className="included-section">
          <h2>Включено в стоимость</h2>
          <ul className="included-list">
            {card.includes.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </section>
      )}
      {card.map?.enabled && (card.map.points || []).length > 0 && (
        <section className="card-map-section" style={{ padding: '40px 0' }}>
          <div className="card-container">
            <h2 style={{ marginBottom: 20 }}>Маршрут</h2>
            <YandexMap
              points={card.map.points}
              showRoute={card.map.showRoute !== false}
              height={420}
            />
          </div>
        </section>
      )}
      {(card.gallery || []).length > 0 && (
        <AutoCarousel
          images={card.gallery}
          title="Галерея"
          interval={5000}
          className="auto-carousel--card"
        />
      )}
      {selectedImage && (
        <div className="image-modal">
          <div className="card-modal-overlay" onClick={() => setSelectedImage(null)} />
          <div className="card-modal-content">
            <button className="close-btn" onClick={() => setSelectedImage(null)}>×</button>
            <img src={selectedImage} alt={`Фото ${currentImageIndex + 1}`} />
            {allImages.length > 1 && (
              <>
                <button className="nav-btn prev" onClick={prevImage}>❮</button>
                <button className="nav-btn next" onClick={nextImage}>❯</button>
              </>
            )}
          </div>
        </div>
      )}
    </main>
    <section className="cta-section">
      <h2>Остались вопросы?</h2>
      <p>Напишите нам — расскажем подробнее и поможем с записью. Ответим в течение дня.</p>
      <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>Задать вопрос</button>
    </section>
    <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle={card?.title} />
    <BookingModal
      isOpen={isBookingOpen}
      onClose={() => setIsBookingOpen(false)}
      card={cardForBooking}
      preselectedDate={nearestDate(card?.availableDates)}
    />
    </>
  );
};

export default CardPage;
